/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { SetStateAction, useCallback, useMemo, useState } from 'react'
import usePaginationQuery from '../../../../client/js/components/pagination/usePaginationQuery'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  createMissingInvoices,
  deleteInvoice,
  downloadInvoicesArchive,
  findManyInvoices
} from '../../lib/rest/invoiceAPIClient'
import { getHalEmbedded, getHalPageTotalElements } from '@jalik/hal'
import { QueryParams } from '../../../../client/js/hooks/useQueryParams'
import useSelection from '../../../../client/js/components/selection/useSelection'
import SelectionAlert from '../../../../client/js/components/selection/SelectionAlert'
import LoadingHelper from '../../../../client/js/components/LoadingHelper'
import InvoiceList from './InvoiceList'
import DeleteInvoicesModal from './DeleteInvoicesModal'
import ListCard from '../../../../client/js/components/ListCard'
import Filters from '../../../../client/js/components/Filters'
import InvoiceStatusFilter from '../filters/InvoiceStatusFilter'
import InvoiceTypeFilter from '../filters/InvoiceTypeFilter'
import RequirePerm from '../../../auth/components/RequirePerm'
import { FormattedMessage, useIntl } from 'react-intl'
import SearchForm from '../../../../client/js/components/search/SearchForm'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSyncAlt } from '@fortawesome/free-solid-svg-icons/faSyncAlt'
import { faFilter } from '@fortawesome/free-solid-svg-icons/faFilter'
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash'
import { faDownload } from '@fortawesome/free-solid-svg-icons/faDownload'
import { faPencilRuler } from '@fortawesome/free-solid-svg-icons/faPencilRuler'
import QuickMenu from '../QuickMenu'
import { getInvoiceCreateLink } from '../../lib/links/invoiceLinks'
import { getTemplatesSettingsLink } from '../../lib/links/settingsLinks'
import CustomerInput from '../inputs/CustomerInput'
import Filter from '../Filter'
import { handleValueChange } from '../../../../client/js/lib/form'
import { Button, Menu } from '@mantine/core'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import Toolbar from '../../../../client/js/components/Toolbar'

export type InvoiceListQuery = {
  limit?: string,
  page?: string,
  search?: string,
  customerId?: string,
  status?: string[],
  type?: string[],
  month?: string,
  leaseId?: string
}

export type InvoiceListCardProps = {
  createLink?: string;
  onQueryChange (query: SetStateAction<InvoiceListQuery>): void;
  query: InvoiceListQuery;
}

const initialQuery: InvoiceListQuery = {
  limit: '10',
  page: '1'
}

function InvoiceListCard (props: InvoiceListCardProps) {
  const {
    createLink = getInvoiceCreateLink(),
    query,
    onQueryChange
  } = props

  const intl = useIntl()
  const [totalElements, setTotalElements] = useState<number>(0)
  const pagination = usePaginationQuery(totalElements)
  const { page, limit } = pagination

  const queryParams = useMemo(() => {
    return {
      ...initialQuery,
      ...query,
      page,
      limit
    }
  }, [limit, page, query])

  // Items loading.
  const listQuery = useQuery({
    enabled: true,
    queryKey: ['invoices', queryParams],
    queryFn: () => findManyInvoices(queryParams).then((r) => {
      setTotalElements(getHalPageTotalElements(r))
      return r
    })
  })
  const items = useMemo(() => (
    (listQuery.data != null ? getHalEmbedded(listQuery.data)?.invoices : null) || []
  ), [listQuery.data])

  const setFilter = useCallback((name: string, value?: unknown) => {
    onQueryChange((s: QueryParams) => ({ ...s, page: '1', [name]: value }))
  }, [onQueryChange])

  const setSearch = useCallback((search: string) => {
    setFilter('search', search)
  }, [setFilter])

  // Items selection.
  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const selection = useSelection<number>({
    multiple: true,
    items: selectedItems,
    onChange: setSelectedItems,
    source: items.map((el) => el.id)
  })

  // Items deletion.
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const toggleDeleteModal = useCallback(() => {
    setIsDeleteModalOpen((s) => !s)
  }, [])

  // Items filtering.
  const [isFiltersVisible, setIsFiltersVisible] = useState(true)
  const toggleFilters = useCallback(() => {
    setIsFiltersVisible((s) => !s)
  }, [])

  const generator = useMutation({
    mutationKey: ['generateMissingInvoices', queryParams.leaseId],
    mutationFn: async () => createMissingInvoices(queryParams.leaseId != null
      ? { id: parseInt(queryParams.leaseId, 10) }
      : undefined
    ),
    onSuccess: () => listQuery.refetch()
  })

  const downloader = useMutation({
    mutationKey: ['downloadInvoices', selection.items, query],
    mutationFn: async () => downloadInvoicesArchive({ ...query, ids: selection.items })
  })

  return (
    <ListCard
      filters={<Filters in={isFiltersVisible}>
        <InvoiceStatusFilter
          onChange={setFilter}
          value={queryParams.status}
        />
        <InvoiceTypeFilter
          onChange={setFilter}
          value={queryParams.type}
        />
        <Filter>
          <CustomerInput
            onChange={handleValueChange('customerId', setFilter)}
            value={queryParams.customerId}
          />
        </Filter>
      </Filters>}
      pagination={pagination}
      selection={selection}
      selectionActions={<SelectionAlert>
        <RequirePerm
          permissions={['write:invoice']}
          render={() => (
            <Button onClick={toggleDeleteModal}>
              <FontAwesomeIcon
                fixedWidth
                icon={faTrash}
              />
            </Button>
          )}
        />
      </SelectionAlert>}
      toolbar={<Toolbar>
        <RequirePerm
          permissions={['write:invoice']}
          render={() => (
            <Button
              variant="filled"
              component={AnchorLink}
              to={createLink}
            >
              <FormattedMessage
                id="create_invoice"
                defaultMessage="Créer une facture"
              />
            </Button>
          )}
        />
        <SearchForm
          initialValue={queryParams.search}
          onSubmit={setSearch}
          submitting={listQuery.isFetching}
        />
        <Button
          loading={listQuery.isFetching}
          onClick={() => {
            listQuery.refetch()
          }}
        >
          <FontAwesomeIcon
            fixedWidth
            icon={faSyncAlt}
          />
        </Button>
        <Button
          // todo active={isFiltersVisible}
          onClick={toggleFilters}
        >
          <FontAwesomeIcon
            fixedWidth
            icon={faFilter}
          />
        </Button>
        <RequirePerm
          permissions={['read:invoice']}
          render={() => (
            <Button
              disabled={downloader.isPending || items.length === 0}
              loading={downloader.isPending}
              onClick={() => {
                downloader.mutate()
              }}
            >
              <FontAwesomeIcon
                fixedWidth
                icon={faDownload}
              />
            </Button>
          )}
        />
        <RequirePerm
          permissions={['read:invoice-template', 'write:invoice-template']}
          render={() => (
            <Button
              component={AnchorLink}
              to={`${getTemplatesSettingsLink()}#invoices`}
              title={intl.formatMessage({
                id: 'invoice_templates',
                defaultMessage: 'Modèles de facture'
              })}
            >
              <FontAwesomeIcon
                fixedWidth
                icon={faPencilRuler}
              />
            </Button>
          )}
        />

        <RequirePerm
          permissions={['write:invoice']}
          render={() => (
            <QuickMenu buttonProps={{ variant: 'light' }}>
              <Menu.Item
                disabled={generator.isPending}
                onClick={() => {
                  generator.mutate()
                }}
              >
                <FormattedMessage
                  id="generate_missing_invoices"
                  defaultMessage="Générer les factures manquantes"
                />
              </Menu.Item>
            </QuickMenu>
          )}
        />
      </Toolbar>}
    >
      <LoadingHelper
        query={listQuery}
        render={() => (
          <InvoiceList items={items} />
        )}
      />

      {isDeleteModalOpen
        ? <DeleteInvoicesModal
          opened={isDeleteModalOpen}
          items={selection.items}
          onClose={toggleDeleteModal}
          onConfirm={deleteInvoice}
          onSuccess={() => {
            selection.clear()
            listQuery.refetch()
          }}
        />
        : null}
    </ListCard>
  )
}

export default InvoiceListCard
